import Api from 'fou-api'
import store from 'src/vuex/'
import { router } from 'src/config/router/'
import { AUTH_LOGOUT_URL_FOR_UNAUTHORISED_USER } from 'src/utils/constants/auth0'
import i18n from 'src/assets/i18n/i18n'

let endPoints = ['tags', 'designers', 'allFoundries']
const setApi = () => {
  const { t } = i18n().global
  const CancelToken = Api.CancelToken
  const source = CancelToken.source()
  // Request interceptor
  Api.interceptors.request.use(function (config) {
    //  Generate cancel token source
    let source = Api.CancelToken.source()

    // Set cancel token on this request
    config.cancelToken = source.token

    // Add to vuex to make cancellation available from anywhere
    store.commit('auth/addCancelToken', source)
    store.commit('auth/cancelSameAPITokens', endPoints);
    store.commit('auth/addSameAPITokens', { config, source })
    return config
  }, function (error) {
    return Promise.reject(error)
  })
  // Redirect to login in case of any 401 error
  Api.interceptors.response.use(
    response => {
      return response
    },
    function (error) {
      if (!Api.isCancel(error)) {
        // handle error
        let err = null
        let status = ((error || {}).response || {}).status
        const file = ((((error || {}).response || {}).data || {}).errors || {}).file || ''
        switch (status) {
          /**
           * Do not present error message; console output only
           * [Exception: use back-end message if available]
           */
          case 400:
            if (
              error.response.data.errorKey === 'THE_CONTRACT_IS_ALREADY_SIGNED' ||
              error.response.data.errorKey === 'CONTRACT_STATUS_NOT_FOUND'
            ) {
              status = 999 // hide error message
              router.replace({ name: '403' })
              break
            } else if (error.response.data.errorKey === 'IN_REVIEW_SUBMISSIONS_LIMIT_REACHED') {
              status = 999 // hide error message
              store.commit('submissions/setLimitError')
              break
            } else if (error.response.data.errorKey === 'AS_A_FOUNDRY_ADMIN_YOUR_PERMISSIONS_ARE_PREDEFINED_AND_CANNOT_BE_CHANGED') {
              status = 999 // hide error message
              err = (((error || {}).response || {}).data || {}).message || null
              break
            } else if (error.response.data.errorKey === 'SUBMISSION_IS_ALREADY_BEING_PROCESSED') {
              router.replace({ path: '/manage-fonts' })
              err = t('api.errors.submission_in_edit_state')
              break
            } else {
              const msg = (((error || {}).response || {}).data || {}).message || null
              if (msg) {
                err = msg
              } else {
                console.warn(error)
                status = 999 // hide error message
              }
              // Only for Payment Admin
              if (
                [
                  'payment_edit_data_manager',
                  'payment_new_data_manager',
                  'payment_accounts',
                  'payment_dashboard'
                ].includes(router.currentRoute.value.name)
              ) {
                router.replace({ name: '400' })
              }
              break
            }
          /**
           * Redirect to login page
           */
          case 401:
            source.cancel('Operation canceled due to unauthorised user.') // To cancel other API request

            if (router.currentRoute.value.name !== 'login') {
              store.commit('auth/setAuth')
              store.commit('auth/clearStorage')
              window.location.href = AUTH_LOGOUT_URL_FOR_UNAUTHORISED_USER
            } else {
              window.location.href = AUTH_LOGOUT_URL_FOR_UNAUTHORISED_USER
              err = t('api.errors.400')
            }
            break

          case 403:
            if (window.localStorage.getItem('application_status') === 'new') {
              router.replace({ path: '/get-started' })
            } else {
              router.replace({ name: '403' })
            }
            break
          /**
           * Resource not found, redirect to home
           */
          case 404:
            if (
              error.response.data.errorKey === 'FOUNDRYID_DOES_NOT_EXIST' ||
              error.response.data.errorKey === 'THIS_FOUNDRY_DOES_NOT_HAVE_ANY_SIGNED_CONTRACT'
            ) {
              status = 999
              router.replace({ name: '403' })
              break
            } else {
              err = t('api.errors.404')
              // only for no user found for user management page to show the toaster to remain intact on that page only.
              // for others it will redirect to 404 page
              if (error.response.data.errorKey === 'NO_USER_FOUND') {
                return;
              }
              router.replace({ name: '404' })
              break
            }
          case 409:
            if (error.response.data.errorKey === 'FAMILY_DISPLAY_NAMES_ALREADY_EXIST' || error.response.data.errorKey === 'STYLE_NAMES_MUST_BE_UNIQUE') {
              err = error.response.data;
            } else if (error.response.data.errorKey === 'create_submission_not_live' || error.response.data.errorKey === 'non_custom_ingestion' || error.response.data.errorKey === 'no_binaries_with_status_updated_or_new' || error.response.data.errorKey === 'family_name_not_unique' || error.response.data.errorKey === 'FAMILY_NAME_ALREADY_EXISTS' || error.response.data.errorKey === 'CANNOT_SPLIT_FAMILY_SUBMISSION_IS_ALREADY_LIVE' || error.response.data.errorKey === 'SUBMISSION_ALREADY_EXISTS_IN_INGESTION') {
              err = error.response.data.message;
            } else {
              err = t('api.errors.409');
            }
            break

          case 412:
            err = t('api.errors.412')
            // Not for Payment Admin Dashboard
            if (router.currentRoute.value.name !== 'payment_dashboard') {
              router.replace({ name: '412' })
            }
            break

          case 413:
            err = error.response.data
            break

          case 415:
            if (error.response.data.errorKey === 'NOT_A_TTF_OR_OTF_FONT_FILE') {
              err = file ? t('api.errors.415', { file }) : t('api.errors.415_no_filename')
              break
            } else if (error.response.data.errorKey === 'EXTENSION_NOT_SPECIFIED') {
              err = t('api.errors.415_ext_missing', { file })
              break
            } else {
              err = error.response.data
              break
            }

          case 422:
            if (error.response.data.messsage === 'Unprocessable Entity') {
              err = t('api.errors.422')
            } else {
              err = error.response.data
            }
            break

          case 429:
            if (error.response.data.errorKey.includes('YOUVE_REACHED_YOUR_MONTHLY_AI_WRITING_SUPPORT_LIMIT')) {
              err = error.response.data;
              err.errorType = 'RATE_LIMIT_EXCEEDED';
            } else {
              err = t('api.errors.429')
            }
            break

          case 502:
            err = t('api.errors.502')
            break

          case 503:
            err = t('api.errors.503')
            break

          case 504:
            err = t('api.errors.504')
            break

          case 424:
            router.replace({ name: '424' })
            break
          case 423:
              err = error.response.data
              break
          default:
            router.replace({ name: '500' })
            err = t('api.errors.500')
            break
        }
        /**
         * rebuild error object structure in case of empty server response
         */
        Object.assign(error, { response: { data: { error: err }, status } })
        return Promise.reject(error)
      }
      if (error.message !== 'canceled') {
        return Promise.reject(error)
      } else {
        return Promise.reject()
      }
    }
  )
}

export default setApi
